import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  AssortmentService,
  AuthService,
  BackendInterceptor,
  BaseViewConfigService,
  CartService,
  Customer,
  CustomerService,
  DocumentsService,
  GungCompanyService,
  GungFlow,
  GungFlowService,
  GungModalService,
  NavAction,
  NavbarConfigService,
  NavGroup,
  NavigationConfig,
  NavLink,
  NewsActionConfigService,
  PriceConfigService,
  SelectedCustomerService,
  LocationConfigService,
  MetadataService,
  SelectedSupplierService,
  GungNavbarService
} from 'gung-standard';
import { Location } from '@angular/common';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, first, map, mergeMap, switchMap } from 'rxjs';
import { BackendFeatureService } from 'gung-standard';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class CultNavbarConfigService extends NavbarConfigService {
  public currentFlow: GungFlow;
  isReturNoPrice: boolean;
  
  enableMyCompany = true;
  enableActivityInternal = true;

  public showFlowsAsDropdown = false;
  
  constructor(
    protected authService: AuthService,
    protected gungFlowService: GungFlowService,
    protected translateService: TranslateService,
    protected assortmentService: AssortmentService,
    protected selectedCustomerService: SelectedCustomerService,
    protected router: Router,
    protected gungModalService: GungModalService,
    protected cartService: CartService,
    protected customerService: CustomerService,
    protected newsActionConfigService: NewsActionConfigService,
    protected backendInterceptor: BackendInterceptor,
    protected documentsService: DocumentsService,
    protected priceConfigService: PriceConfigService,
    protected location: Location,
    protected baseViewConfigService: BaseViewConfigService,
    protected gungCompanyService: GungCompanyService,
    @Inject('environment')
    protected environment: NavigationConfig,
    protected locationConfigService: LocationConfigService,
    metadataService: MetadataService,
    selectedSupplierService: SelectedSupplierService,
    protected backendFeatureService: BackendFeatureService,
    protected http: HttpClient,
    protected gungNavbar: GungNavbarService
  ) {
    super(
      authService,
      gungFlowService,
      translateService,
      selectedCustomerService,
      router,
      gungModalService,
      cartService,
      customerService,
      newsActionConfigService,
      backendInterceptor,
      documentsService,
      priceConfigService,
      location,
      baseViewConfigService,
      gungCompanyService,
      assortmentService,
      environment,
      locationConfigService,
      metadataService,
      selectedSupplierService,
      backendFeatureService,
      http,
      gungNavbar
    );
  }

  public getLeftMenus(): Observable<NavGroup[]> {
    const baseMenu: NavGroup[] = [];
    return this.authService.getCurrentUser().pipe(
      filter(user => !!user),
      first(),
      switchMap(user =>
        forkJoin([
          this.gungFlowService.getGungFlows().pipe(first()),
          this.assortmentService.getAssortmentRecursive(user.assortment).pipe(first()),
          this.userRoles().pipe(
            first(),
            map(roles => roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1)
          )
        ])
      ),
      map(([flows, assortment, isSalesAdmin]) => {
        const flowMenu = flows.map(flow => {
          const assortments = JSON.parse(JSON.stringify(assortment.children));
          if (assortment.extra?.pim?.dropdown) {
            return {
              heading: flow.name,
              links: assortments?.map(childAssortment => ({
                heading: childAssortment.name,
                link: `/categories/${childAssortment.id}`,
                extra: childAssortment.extra,
                action: () =>  {
                  this.selectFlow(flow.id);
                },
              })) || undefined
            } as NavGroup;
          }
          return {
            heading: flow.name,
            link: `/products`,
            action: () =>  {
              this.selectFlow(flow.id);
            }
          } as NavGroup;
        });
        
        baseMenu.push(...flowMenu);

        const activities = [
          {
          heading: 'OPENED_ACTIVITIES',
          link: '/activity-internal/opened',
        },
        {
          heading: 'EXPIRED_ACTIVITIES',
          link: '/activity-internal/expired',
        },
        {
          heading: 'ALL_ACTIVITIES',
          link: '/activity-internal/all',
        }
        ] as NavGroup[];

        if(isSalesAdmin){
          baseMenu.push(...activities)
        }
        
        return baseMenu;
      })
    );
  }
  public getMainMenu(): Observable<NavGroup> {
    return this.userRoles().pipe(
      mergeMap(roles =>
        forkJoin([
          of(roles),
          this.selectedCustomerService.getSelectedCustomer().pipe(
            filter(customer => !!customer),
            first()
          ),
          this.customerService.getCustomers().pipe(
            filter(customer => !!customer),
            first()
          ),
          this.newsActionConfigService.getIsEnabledShowUserNewsInPage().pipe(first()),
          this.enableDocumentUserGroups ? this.getUserDocuments().pipe(first()) : of([]),
          this.authService.getCurrentUser().pipe(first())
        ])
      ),
      map(([roles, customer, customers, isEnabledShowUserNewsInPage, documents, user]) => {
        this.isEnabledShowUserNewsInPage = isEnabledShowUserNewsInPage;
        this.documents = documents;
        const mainMenu: NavGroup = {
          heading: '',
          links: [
            {
              link: undefined,
              heading: 'LOGOUT',
              // icon: 'fa-file-invoice-dollar',
              action: () => {
                this.logout();
              }
            }
          ]
        };
        this.isReturNoPrice = user.roles.includes('RETUR_NOPRICE');
        if (roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1) {
          if (roles.indexOf('ADMIN') > -1) {
            mainMenu.links.unshift(...this.getAdminMenu(roles.indexOf('ACTUATOR') > -1));
          }
          mainMenu.links.unshift(...this.getSalesMenu(roles.indexOf('ACTUATOR') > -1, roles, user.extra.salesCodeErp));
        }
        mainMenu.links.unshift(
          ...this.getUserMenu(customer, roles.indexOf('SALES') > -1, customers.length, roles.indexOf('ACTUATOR') > -1)
        );

        if (this.priceConfigService.enableHidePrice) {
          const hidePriceSwitch: NavGroup = {
            link: undefined,
            heading: 'HIDE_PRICE',
            action: (inputElement: HTMLInputElement) => {
              this.priceConfigService.setHidePrice(inputElement.checked);
            }
          };
          mainMenu.links.push(hidePriceSwitch);
        }

        return mainMenu;
      })
    );
  }
  protected getUserMenu(
    customer: Customer,
    isSales?: boolean,
    customers?: number,
    isActuator?: boolean
  ): (NavLink | NavAction | NavGroup)[] {
    if (!this.isReturNoPrice) {
      const usermenu = super.getUserMenu(customer, isSales, customers, isActuator);
      const index = (usermenu[0] as NavGroup).links.findIndex(l => l.heading === 'ACTIVITIES');
      if(index > - 1){
        (usermenu[0] as NavGroup).links.splice(index, 1);
      }
      return usermenu;
    
    }
    const menuLinks: NavGroup[] = [
      {
        heading: '',
        links: [
          {
            link: `${'/customers/' + customer.id}`,
            heading: 'MY_PAGES'
            // icon: 'fa-user'
          },
          {
            link: '/delivery-location',
            heading: 'DELIVERY_LOCATION'
            // icon: 'fa-folder-open'
          },
          {
            link: '/orders',
            heading: 'PREVIOUS_ORDERS'
            // icon: 'fa-folder-open'
          },
          {
            link: '/products-export',
            heading: 'PRODUCT_EXPORT_NAV_BAR'
            // icon: 'fa-file-download'
          },
          /* {
        link: 'getProductListBaseRoute()',
        heading: 'SHOP_BY_PRODUCT_LIST',
        // icon: 'fa-list-alt'
      }, */
          // {
          //   link: '/carts',
          //   heading: 'SAVED_CARTS'
          // },
          {
            link: '/saved-quotes',
            heading: 'SAVED_QUOTES'
          },
          {
            link: '/favourites',
            heading: 'FAVOURITES'
          }
        ]
      }
    ];

    if (!isSales) {
      // Remove SAVED_QUOTES if not SALES
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'SAVED_QUOTES'),
        1
      );
    } else {
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'MY_PAGES'),
        1
      );
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'DELIVERY_LOCATION'),
        1
      );
    }

    if (customers && customers < 2) {
      // Remove DELIVERY_LOCATION if only one delivery location
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'DELIVERY_LOCATION'),
        1
      );
    }

    let favouritesIndex = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'FAVOURITES');
    if (this.enableDigitalAssets) {
      // Add digital assets to the project
      (menuLinks[0] as NavGroup).links.splice(++favouritesIndex, 0, {
        link: '/digital-assets',
        heading: 'DIGITAL_ASSETS'
      });
    }

    /* if (this.enableActivityInternal && isSales) {
      // Add activity internal to the project
      (menuLinks[0] as NavGroup).links.splice(++favouritesIndex, 0, {
        link: '/activity-internal',
        heading: 'ACTIVITIES'
      });
    } */

    if (this.isEnabledShowUserNewsInPage) {
      (menuLinks[0] as NavGroup).links.push({ link: '/news', heading: 'NEWS' });
    }

    if (this.enableDocumentUserGroups) {
      this.documents.forEach(doc => {
        const url = this.backendInterceptor.getBaseUrl() + '/gung-document-archive/document/' + doc.id;
        menuLinks[0].links.push({
          heading:
            doc.metaData.description && doc.metaData.description !== 'undefined'
              ? doc.metaData.description
              : doc.filename,
          action: () => {
            window.open(url, '_blank');
          }
        });
      });
    }

    if (this.enableWhiteboardManagement) {
      (menuLinks[0] as NavGroup).links.push({ link: '/whiteboard-management', heading: 'WHITEBOARD' });
    }

    return menuLinks;
  }

  protected getSalesMenu(isActuator?: boolean, roles?: string[], salesCodeErp?: string): (NavLink | NavAction | NavGroup)[] {
    const menu = super.getSalesMenu(isActuator, roles, salesCodeErp);
    (menu[0] as NavGroup).links.push({ link: '/activity-internal', heading: 'ACTIVITIES' });
    return menu;
  }
}
